import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, Check, X } from "lucide-react";
import { toast } from "sonner";

// Hlášení spotřeby materiálu z odhlášení dělníků — blok na pracovním Přehledu.
// „Odepsat" provede výdej na zakázku (jediný dopad na sklad);
// „Už odepsáno" hlášení jen odškrtne (pohyb byl zaznamenán ručně).
export default function MaterialApproval() {
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null); // {rep, qty}
  const [dismiss, setDismiss] = useState(null); // {rep, note}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/material-reports?status=pending").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doConfirm = async () => {
    if (!confirm || Number(confirm.qty) <= 0) return toast.error("Zadejte kladné množství.");
    setBusy(true);
    try {
      await api.post(`/material-reports/${confirm.rep.id}/confirm`, { qty: Number(confirm.qty) });
      toast.success("Materiál odepsán ze skladu.");
      setConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDismiss = async () => {
    setBusy(true);
    try {
      await api.post(`/material-reports/${dismiss.rep.id}/dismiss`, { note: dismiss.note || null });
      toast.success("Hlášení odškrtnuto (bez pohybu skladu).");
      setDismiss(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-[20px] p-4" data-testid="material-approval">
      <h2 className="font-bold flex items-center gap-2 mb-3"><Package size={18} className="text-brand" /> Materiál k odepsání ({rows.length})</h2>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} data-testid={`material-report-${r.id}`}
            className="flex items-center gap-3 p-3 rounded-[14px] border border-border flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{r.name} — <span className="tabular-nums">{r.qty} {r.unit}</span></div>
              <div className="text-xs text-muted-foreground truncate">
                {r.user_name} · {r.project_name} · {formatDate(r.date)}
                {r.stock_available != null && <> · skladem {r.stock_available} {r.unit}</>}
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button size="sm" onClick={() => setConfirm({ rep: r, qty: r.qty })}
                data-testid={`material-confirm-${r.id}`} className="h-9 flex-1 sm:flex-none font-bold bg-success hover:bg-success/90 text-white">
                <Check size={14} /> Odepsat
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDismiss({ rep: r, note: "" })}
                data-testid={`material-dismiss-${r.id}`} className="h-9 flex-1 sm:flex-none">
                <X size={14} /> Už odepsáno
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Potvrzení odpisu — možnost upravit množství */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Odepsat ze skladu</DialogTitle><DialogDescription className="sr-only">Potvrzení výdeje materiálu</DialogDescription></DialogHeader>
          {confirm && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{confirm.rep.name} · nahlásil {confirm.rep.user_name} · zakázka {confirm.rep.project_name}</p>
              <div>
                <Label>Množství ({confirm.rep.unit})</Label>
                <Input type="number" min={0} value={confirm.qty} onChange={(e) => setConfirm({ ...confirm, qty: e.target.value })}
                  className="h-11 mt-1 w-full min-w-0" data-testid="material-confirm-qty" />
                {confirm.rep.stock_available != null && (
                  <p className="text-[11px] text-muted-foreground mt-1">Skladem: {confirm.rep.stock_available} {confirm.rep.unit}. Provede se výdej na zakázku (náklad zakázky).</p>
                )}
              </div>
              <Button onClick={doConfirm} disabled={busy} className="w-full h-11 font-bold" data-testid="material-confirm-save">Odepsat ze skladu</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Odškrtnutí bez pohybu */}
      <Dialog open={!!dismiss} onOpenChange={(o) => !o && setDismiss(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Už odepsáno / ignorovat</DialogTitle><DialogDescription className="sr-only">Odškrtnutí hlášení bez pohybu skladu</DialogDescription></DialogHeader>
          {dismiss && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Hlášení „{dismiss.rep.name} — {dismiss.rep.qty} {dismiss.rep.unit}" se odškrtne <b>bez pohybu skladu</b>. Použijte, když už je výdej zaznamenaný ručně.</p>
              <div><Label>Poznámka (nepovinné)</Label><Input value={dismiss.note} onChange={(e) => setDismiss({ ...dismiss, note: e.target.value })} className="h-11 mt-1" placeholder="např. vydáno ráno ručně" data-testid="material-dismiss-note" /></div>
              <Button variant="outline" onClick={doDismiss} disabled={busy} className="w-full h-11 font-bold" data-testid="material-dismiss-save">Odškrtnout</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
